import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Paper,
    Chip,
} from "@mui/material";
import {
    SendSharp,
    EmailSharp,
    ArrowForward,
    People,
    MonitorHeart,
    EventNote,
} from "@mui/icons-material";
import { MdWhatsapp } from "react-icons/md";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import { formatRoleLabel } from "../UserManagement/roleLabels";
import { ADMIN_BASE_PATH } from "./adminNavConfig";
import {
    ASSIGNABLE_ROLES,
    computeRoleStats,
    fetchAllAdminUsers,
    filterUsersBySection,
} from "./adminUtils";
import { getServiceStatus } from "../../api/superAdminApi";

const QuickLinkCard = ({ title, count, description, to, icon, accent, extra }) => (
    <Card
        elevation={0}
        component={RouterLink}
        to={to}
        sx={{
            ...WB_SX.card,
            bgcolor: WB_UI.color.cardBgStrong,
            textDecoration: "none",
            color: "inherit",
            height: "100%",
            borderLeft: `4px solid ${accent}`,
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" },
        }}
    >
        <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: `${accent}18`, color: accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: accent }}>{count}</Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: extra ? 1 : 2, lineHeight: 1.6 }}>{description}</Typography>
            {extra}
            <Button size="small" endIcon={<ArrowForward fontSize="small" />} sx={{ textTransform: "none", fontWeight: 600, px: 0, mt: 1 }}>
                Manage
            </Button>
        </CardContent>
    </Card>
);

const HealthStat = ({ label, healthy, unhealthy, stale }) => (
    <Box sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: "rgba(15,23,42,0.04)" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            <Chip size="small" label={`${healthy} healthy`} sx={{ bgcolor: "rgba(16,185,129,0.12)", color: "#047857", fontWeight: 600 }} />
            <Chip size="small" label={`${unhealthy} unhealthy`} sx={{ bgcolor: "rgba(239,68,68,0.1)", color: "#b91c1c", fontWeight: 600 }} />
            {stale > 0 && <Chip size="small" label={`${stale} stale`} variant="outlined" />}
        </Box>
    </Box>
);

const AdminOverview = () => {
    const [users, setUsers] = useState([]);
    const [serviceStatus, setServiceStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [userData, status] = await Promise.all([
                fetchAllAdminUsers(),
                getServiceStatus().catch(() => null),
            ]);
            setUsers(userData);
            setServiceStatus(status);
        } catch (err) {
            setError(err?.message || "Failed to load overview data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => computeRoleStats(users), [users]);
    const personalCount = useMemo(() => filterUsersBySection(users, "personal").length, [users]);
    const emailCount = useMemo(() => filterUsersBySection(users, "email").length, [users]);
    const wbCount = useMemo(() => filterUsersBySection(users, "whatsapp-business").length, [users]);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200 }}>
            <Typography variant="h5" sx={{ ...WB_SX.pageTitle, mb: 0.5 }}>Overview</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640, lineHeight: 1.65 }}>
                Platform admin portal powered by the Super-Admin API. Manage users, credits, subscriptions, and monitor service health.
            </Typography>

            {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

            {loading ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                    <CircularProgress size={32} sx={{ color: WB_UI.color.primaryButtonBg }} />
                </Box>
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <QuickLinkCard title="Personal WhatsApp" count={personalCount} description="Users with PERSONAL role and messaging credits." to={`${ADMIN_BASE_PATH}/personal`} icon={<SendSharp />} accent="#1d4ed8" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <QuickLinkCard title="Business email" count={emailCount} description="Email workers, domains, senders, and BEC credits." to={`${ADMIN_BASE_PATH}/email`} icon={<EmailSharp />} accent="#6d28d9" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <QuickLinkCard title="WhatsApp Business" count={wbCount} description="WB admins, users, agents, and chat operators." to={`${ADMIN_BASE_PATH}/whatsapp-business`} icon={<MdWhatsapp size={22} />} accent="#047857" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <QuickLinkCard title="All users" count={stats.total} description="Full directory with search and role management." to={`${ADMIN_BASE_PATH}/all-users`} icon={<People />} accent="#111827" />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <QuickLinkCard
                                title="Subscriptions"
                                count="—"
                                description="View and manage subscription end dates across all services."
                                to={`${ADMIN_BASE_PATH}/subscriptions`}
                                icon={<EventNote />}
                                accent="#7c3aed"
                                extra={<Typography variant="caption" color="text.secondary">Cross-service subscription management</Typography>}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <QuickLinkCard
                                title="Service health"
                                count={serviceStatus ? (serviceStatus.email?.unhealthy || 0) + (serviceStatus.whatsapp_business?.unhealthy || 0) + (serviceStatus.personal_whatsapp?.unhealthy || 0) : "—"}
                                description="Monitor script heartbeats and unhealthy service instances."
                                to={`${ADMIN_BASE_PATH}/service-health`}
                                icon={<MonitorHeart />}
                                accent="#dc2626"
                                extra={serviceStatus ? (
                                    <Typography variant="caption" color="error.main">Unhealthy instances detected</Typography>
                                ) : null}
                            />
                        </Grid>
                    </Grid>

                    {serviceStatus && (
                        <Paper elevation={0} sx={{ ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong, p: 2.5, mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Service health snapshot</Typography>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={4}>
                                    <HealthStat label="Email" healthy={serviceStatus.email?.healthy ?? 0} unhealthy={serviceStatus.email?.unhealthy ?? 0} stale={serviceStatus.email?.stale ?? 0} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <HealthStat label="WhatsApp Business" healthy={serviceStatus.whatsapp_business?.healthy ?? 0} unhealthy={serviceStatus.whatsapp_business?.unhealthy ?? 0} stale={serviceStatus.whatsapp_business?.stale ?? 0} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <HealthStat label="Personal WhatsApp" healthy={serviceStatus.personal_whatsapp?.healthy ?? 0} unhealthy={serviceStatus.personal_whatsapp?.unhealthy ?? 0} stale={serviceStatus.personal_whatsapp?.stale ?? 0} />
                                </Grid>
                            </Grid>
                            {serviceStatus.timestamp && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                                    Last updated: {new Date(serviceStatus.timestamp).toLocaleString()}
                                </Typography>
                            )}
                        </Paper>
                    )}

                    <Paper elevation={0} sx={{ ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong, p: 2.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Users by role</Typography>
                        <Grid container spacing={1.5}>
                            {ASSIGNABLE_ROLES.map(({ value }) => (
                                <Grid item xs={6} sm={4} md={3} key={value}>
                                    <Box sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: "rgba(15,23,42,0.04)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Typography variant="body2" color="text.secondary">{formatRoleLabel(value)}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{stats[value] || 0}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default AdminOverview;
